import { ArrowLeftRight } from "lucide-react";

import {
  FileFormatIcon,
  useFileInputId,
  type FileFormat,
} from "@/components/file-drop-zone";

export type FileSlot = {
  label: string;
  format: FileFormat;
  file: File | null;
  onRemove: () => void;
};

const Slot = ({ label, format, file, onRemove }: FileSlot) => {
  const inputId = useFileInputId();

  return (
    <div
      role="group"
      aria-label={label}
      className="flex min-w-0 flex-1 flex-col gap-1 self-stretch"
    >
      <p className="caption-1-semibold text-label-neutral">{label}</p>
      <div className="flex flex-1 flex-col justify-center gap-2 rounded-lg bg-background-gray-alternative p-4">
        {file ? (
          <>
            <div className="flex items-start justify-between">
              <FileFormatIcon format={format} className="size-6" />
              <button
                type="button"
                aria-label={`${label} 제거`}
                className="-m-1.5 shrink-0 rounded-lg p-1.5 transition-colors duration-150 hover:bg-neutral-200"
                onClick={onRemove}
              >
                <img
                  alt=""
                  src="/icons/cancel-rounded.svg"
                  className="size-5"
                />
              </button>
            </div>
            <p
              className="label-1-normal-regular line-clamp-2 break-all text-label-normal"
              title={file.name}
            >
              {file.name}
            </p>
          </>
        ) : (
          // 빈 칸은 label 밖(children)이라 암시적 활성화가 안 닿는다 — htmlFor 로 직접 파일 창을 연다
          <label
            htmlFor={inputId}
            className="label-2-regular cursor-pointer text-center text-label-neutral"
          >
            파일을 업로드해주세요
          </label>
        )}
      </div>
    </div>
  );
};

/**
 * 대사할 파일 두 개를 나란히 보여주는 칸. 업로드 영역(FileDropArea) 안에 children 으로 둔다.
 * `onSwap` 이 없으면 가운데 화살표는 장식이다 — DSD/IXD 처럼 칸이 형식으로 정해지면 바꿀 게 없다.
 */
export const FileSlotPair = ({
  slots: [left, right],
  onSwap,
}: {
  slots: [FileSlot, FileSlot];
  onSwap?: () => void;
}) => (
  <div className="flex flex-col gap-2">
    <div className="h-px w-full bg-line-normal" />
    <div className="flex items-center gap-1">
      <Slot {...left} />
      {onSwap ? (
        <button
          type="button"
          aria-label={`${left.label}과 ${right.label} 바꾸기`}
          className="shrink-0 text-label-neutral transition-colors hover:text-blue-500"
          onClick={onSwap}
        >
          <ArrowLeftRight className="size-[18px]" strokeWidth={1.8} />
        </button>
      ) : (
        <ArrowLeftRight
          aria-hidden
          className="size-[18px] shrink-0 text-label-neutral"
          strokeWidth={1.8}
        />
      )}
      <Slot {...right} />
    </div>
  </div>
);
